import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Monitor, Moon, Search, Sun } from "lucide-react";
import "@/concepts/concepts.css";
import "@/concepts/clinical.css";
import { CommandPalette, GROUP_LABEL, STATUS_LABEL, READY_LABEL } from "@/concepts/shared";

export const D = "clinical";
const PATHS = { today: "/today", workbench: "/workbench", focus: "/focus", participant: "/participant", studies: "/studies", participants: "/participants", admin: "/admin", styles: "/styles", builder: "/studies/edit" };
export const to = (s, qs = "") => `${PATHS[s]}${qs}`;
const KEY = "align-theme";
const mq = () => window.matchMedia("(prefers-color-scheme: dark)");

export const useTheme = () => {
  const [pref, setPref] = useState(() => localStorage.getItem(KEY) || "system");
  const [sys, setSys] = useState(() => (mq().matches ? "dark" : "light"));
  useEffect(() => {
    const m = mq();
    const on = (e) => setSys(e.matches ? "dark" : "light");
    m.addEventListener("change", on);
    return () => m.removeEventListener("change", on);
  }, []);
  const resolved = pref === "system" ? sys : pref;
  useEffect(() => { document.documentElement.setAttribute("data-cm-theme", resolved); window.dispatchEvent(new Event("align-theme")); }, [resolved]);
  const set = (p) => { localStorage.setItem(KEY, p); setPref(p); };
  return { pref, resolved, set };
};

export const ThemeToggle = ({ theme }) => (
  <div className="seg" role="radiogroup" aria-label="Colour theme" data-testid="cm-theme-toggle">
    {[["light", "Light", Sun], ["dark", "Dark", Moon], ["system", "System", Monitor]].map(([k, l, I]) => (
      <button key={k} role="radio" aria-checked={theme.pref === k} onClick={() => theme.set(k)} data-testid={`cm-theme-${k}`} title={`${l} theme`}>
        <I size={14} strokeWidth={2} aria-hidden /><span className="hidden 2xl:inline">{l}</span><span className="sr-only 2xl:hidden">{l}</span>
      </button>
    ))}
  </div>
);

export const Wordmark = () => (
  <Link to={to("today")} className="flex select-none items-center gap-2.5" aria-label="Avenir Align — Today" data-testid="cm-logo">
    <span className="text-[10.5px] font-semibold tracking-[0.32em] text-[color:var(--mute)]">AVENIR</span>
    <span className="h-5 w-px bg-[color:var(--rule-strong)]" />
    <span className="flex items-end text-[23px] font-bold leading-none tracking-[-0.045em]">align<span className="mb-[3px] ml-[2px] h-[6px] w-[6px] bg-[color:var(--accent)]" /></span>
  </Link>
);

const NAV = [["today", "Today"], ["studies", "Studies"], ["participants", "Participants"], ["admin", "Administration"]];

export const Shell = ({ active, fixed, onSearch, children }) => {
  const theme = useTheme();
  const on = { focus: "studies", workbench: "studies", builder: "studies", participant: "participants" }[active] || active;
  return (
    <div className={`c-cm ${fixed ? "flex h-screen flex-col overflow-hidden" : "min-h-screen"}`} data-testid={`cm-${active}`}>
      <header className="sticky top-0 z-30 flex h-[60px] shrink-0 items-stretch gap-8 border-b rule bg-[color:var(--bg)] px-8">
        <div className="flex items-center"><Wordmark /></div>
        <nav className="flex items-stretch gap-6" aria-label="Primary">
          {NAV.map(([k, l]) => (
            <Link key={k} to={to(k)} aria-current={on === k ? "page" : undefined} data-testid={`cm-nav-${k}`}
              className={`flex items-center border-b-2 pt-[2px] text-[14px] font-medium ${on === k ? "border-[color:var(--accent)] text-[color:var(--ink)]" : "border-transparent text-[color:var(--mute)] hover:text-[color:var(--ink)]"}`}>{l}</Link>
          ))}
          <span className="hidden items-center gap-5 pl-2 text-[13px] text-[color:var(--faint)] 2xl:flex" title="Planned modules, not built in Sprint 1">
            <span className="h-4 w-px bg-[color:var(--rule-strong)]" />Communications<span>Intake</span><span>Reports</span>
          </span>
        </nav>
        <div className="ml-auto flex items-center gap-3">
          <button onClick={onSearch} className="field flex w-[230px] items-center gap-2 !py-[7px] text-left text-[13.5px] text-[color:var(--mute)] xl:w-[300px]" data-testid="cm-search">
            <Search size={15} aria-hidden /><span className="truncate">Search participants, studies…</span><span className="kbd ml-auto">⌘K</span>
          </button>
          <ThemeToggle theme={theme} />
          <Tag tone="unk" testId="cm-synthetic">Synthetic data</Tag>
          <span className="flex items-center gap-2 pl-1 text-[13px]" title="Demo Recruiter · no authentication">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[color:var(--ink)] text-[12px] font-semibold text-[color:var(--bg)]">DR</span>
          </span>
        </div>
      </header>
      {children}
    </div>
  );
};

export const Palette = (props) => (
  <CommandPalette dir={D} personTo={(p) => to("participant", `?p=${p.id}`)} {...props}
    contentClass="c-cm rounded-lg border-[color:var(--rule-strong)] bg-[color:var(--raise)] text-[color:var(--ink)]"
    itemClass="text-[14px] data-[selected=true]:bg-[color:var(--accent-soft)] data-[selected=true]:text-[color:var(--ink)]" />
);

const TONES = {
  neutral: "bg-[color:var(--surface)] text-[color:var(--ink-2)]",
  met: "bg-[color:var(--met-soft)] text-[color:var(--met)]",
  fail: "bg-[color:var(--fail-soft)] text-[color:var(--fail)]",
  unk: "bg-[color:var(--unk-soft)] text-[color:var(--unk)]",
  accent: "bg-[color:var(--accent-soft)] text-[color:var(--accent-text)]",
  conflict: "bg-[color:var(--conflict-soft)] text-[color:var(--conflict)]",
  signal: "bg-[color:var(--signal)] text-[color:var(--signal-ink)]",
};
export const Tag = ({ tone = "neutral", children, testId }) => (
  <span data-testid={testId} className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-[4px] px-2 py-[3px] text-[12px] font-medium leading-none ${TONES[tone]}`}>{children}</span>
);

export const Kbd = ({ children, dim }) => <span className={`kbd ${dim ? "opacity-40" : ""}`} aria-hidden>{children}</span>;

const OUTCOME = {
  required: { supported: "met", not_met: "not met", unknown: "unknown" },
  desirable: { supported: "met", not_met: "not met", unknown: "unknown" },
  exclusion: { supported: "clear", not_met: "triggered", unknown: "unconfirmed" },
};
export const outcomeText = (kind, o) => OUTCOME[kind][o];
const OCT = "4.6,1 9.4,1 13,4.6 13,9.4 9.4,13 4.6,13 1,9.4 1,4.6";

export const Mark = ({ outcome, kind = "required", label, size = 14 }) => {
  const c = outcome === "supported" ? "var(--met)" : outcome === "not_met" ? "var(--fail)" : "var(--unk)";
  const solid = outcome !== "unknown";
  const p = { fill: solid ? c : "none", stroke: c, strokeWidth: 1.4, strokeDasharray: solid ? undefined : "2.4 1.6" };
  return (
    <svg width={size} height={size} viewBox="0 0 14 14" role="img" aria-label={`${label || "Criterion"}: ${outcomeText(kind, outcome)}${kind === "exclusion" ? " (exclusion rule)" : ""}`} className="shrink-0">
      <title>{`${label || ""} — ${outcomeText(kind, outcome)}`}</title>
      {kind === "exclusion" ? <polygon points={OCT} {...p} /> : kind === "desirable" ? <circle cx="7" cy="7" r="6" {...p} /> : <rect x="1" y="1" width="12" height="12" rx="2.5" {...p} />}
      {outcome === "supported" && <path d="M4.2 7.3l1.9 1.9 3.8-4.2" fill="none" stroke="var(--bg)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />}
      {outcome === "not_met" && <path d="M4.8 4.8l4.4 4.4M9.2 4.8l-4.4 4.4" stroke="var(--bg)" strokeWidth="1.8" strokeLinecap="round" />}
      {outcome === "unknown" && <circle cx="7" cy="7" r="1.4" fill={c} />}
    </svg>
  );
};

const BARS = { strong: 3, possible: 2, low_priority: 1 };
export const GroupGlyph = ({ group }) => {
  if (group === "excluded") return <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden><circle cx="7" cy="7" r="5.6" fill="none" stroke="var(--fail)" strokeWidth="1.6" /><path d="M3.2 10.8l7.6-7.6" stroke="var(--fail)" strokeWidth="1.6" /></svg>;
  const n = BARS[group];
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden>
      {[0, 1, 2].map((i) => <rect key={i} x={1 + i * 4.6} y={10 - i * 3.5} width="3.2" height={3 + i * 3.5} rx="0.6"
        fill={n == null ? "none" : i < n ? "var(--ink)" : "var(--rule-strong)"} stroke={n == null ? "var(--unk)" : "none"} strokeDasharray={n == null ? "1.6 1.2" : undefined} strokeWidth="1" />)}
    </svg>
  );
};
export const Group = ({ group, size = 14 }) => (
  <span className="inline-flex items-center gap-2 whitespace-nowrap font-medium" style={{ fontSize: size }}><GroupGlyph group={group} />
    <span className={group === "excluded" ? "text-[color:var(--fail)]" : group === "needs_information" ? "text-[color:var(--unk)]" : ""}>{GROUP_LABEL[group]}</span></span>
);

export const ReadyGlyph = ({ r }) => (
  <svg width="13" height="13" viewBox="0 0 14 14" aria-hidden className="shrink-0">
    {r === "ready" && <circle cx="7" cy="7" r="5.5" fill="var(--met)" />}
    {r === "restricted" && <path d="M7 1.8l5.6 10H1.4z" fill="none" stroke="var(--unk)" strokeWidth="1.6" strokeLinejoin="round" />}
    {r === "blocked" && <><circle cx="7" cy="7" r="5.5" fill="none" stroke="var(--fail)" strokeWidth="1.6" /><path d="M3.3 10.7l7.4-7.4" stroke="var(--fail)" strokeWidth="1.6" /></>}
  </svg>
);
export const READY_TONE = { ready: "text-[color:var(--met)]", restricted: "text-[color:var(--unk)]", blocked: "text-[color:var(--fail)]" };
export const Ready = ({ r }) => <span className={`inline-flex items-center gap-1.5 whitespace-nowrap text-[13px] font-medium ${READY_TONE[r]}`}><ReadyGlyph r={r} />{READY_LABEL[r]}</span>;

const STEPS = ["queued", "pending_review", "contacted", "pre_screening", "ready_for_first_visit"];
export const StatusMeter = ({ status }) => {
  if (!status) return <span className="text-[13px] text-[color:var(--mute)]">Not in study</span>;
  if (status === "unsuitable") return <span className="text-[13px] text-[color:var(--mute)] line-through decoration-[color:var(--faint)]">Unsuitable</span>;
  const n = STEPS.indexOf(status) + 1;
  return (
    <span className="flex flex-col gap-1">
      <span className="text-[13px]">{STATUS_LABEL[status]}</span>
      <span className="flex gap-[3px]" aria-hidden>{STEPS.map((s, i) => <span key={s} className={`h-[3px] w-[14px] rounded-full ${i < n ? "bg-[color:var(--accent)]" : "bg-[color:var(--rule-strong)]"}`} />)}</span>
    </span>
  );
};

export const ModeSwitch = ({ mode, studyId }) => (
  <div className="seg" role="tablist" aria-label="Workbench mode">
    {[["workbench", "Overview"], ["focus", "Focus review"]].map(([s, l]) => (
      <Link key={s} to={to(s, `?study=${studyId}`)} role="tab" aria-selected={mode === s} data-testid={`cm-mode-${s}`}>{l}<Kbd>T</Kbd></Link>
    ))}
  </div>
);

export const Legend = () => (
  <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-[12px] text-[color:var(--mute)]" data-testid="cm-legend">
    <span className="flex items-center gap-1.5"><Mark outcome="supported" label="Legend" /> Met</span>
    <span className="flex items-center gap-1.5"><Mark outcome="not_met" label="Legend" /> Confirmed not met</span>
    <span className="flex items-center gap-1.5"><Mark outcome="unknown" label="Legend" /> Unknown, never counted as a failure</span>
    <span className="flex items-center gap-1.5"><Mark outcome="supported" kind="exclusion" label="Legend" /> Exclusion rule (octagon)</span>
    <span className="flex items-center gap-1.5"><Mark outcome="supported" kind="desirable" label="Legend" /> Desirable (circle)</span>
  </div>
);

export const Section = ({ title, meta, children, className = "", testId }) => (
  <section className={className} data-testid={testId}>
    <div className="mb-3 flex items-baseline justify-between gap-4 border-b rule pb-2"><h2 className="h2">{title}</h2>{meta && <span className="text-[13px] text-[color:var(--mute)]">{meta}</span>}</div>
    {children}
  </section>
);
